<script>
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing';
  import { animeAPI } from '../lib/api';
  import Loading from '../components/Loading.svelte';

  export let id;

  let anime = null;
  let episodes = [];
  let loading = true;

  onMount(async () => {
    try {
      if (!id) {
        throw new Error('No anime ID provided');
      }

      const [animeRes, episodesRes] = await Promise.all([
        animeAPI.getById(id),
        animeAPI.getEpisodes(id)
      ]);
      
      anime = animeRes.data?.data || animeRes.data || null;
      episodes = episodesRes.data?.data || episodesRes.data || [];
      
      // Ensure episodes is an array
      if (!Array.isArray(episodes)) {
        console.warn('Unexpected episodes format:', episodes);
        episodes = [];
      }
    } catch (error) {
      console.error('Failed to fetch anime details:', error);
      anime = null;
      episodes = [];
    } finally {
      loading = false;
    }
  });
</script>

{#if loading}
  <Loading />
{:else if anime}
  <div class="min-h-screen">
    <!-- Banner -->
    <div class="relative h-96 overflow-hidden">
      <img 
        src={anime.bannerImage} 
        alt={anime.title}
        class="w-full h-full object-cover"
      />
      <div class="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/50 to-transparent"></div>
    </div>

    <!-- Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10">
      <div class="flex flex-col md:flex-row gap-8">
        <!-- Cover Image -->
        <div class="flex-shrink-0">
          <img 
            src={anime.coverImage} 
            alt={anime.title}
            class="w-64 rounded-lg shadow-2xl"
          />
        </div>

        <!-- Info -->
        <div class="flex-1">
          <h1 class="text-4xl font-bold mb-4">{anime.title}</h1>
          
          <div class="flex flex-wrap gap-4 mb-6">
            <div class="flex items-center gap-2">
              <span class="text-yellow-500">⭐</span>
              <span class="text-xl font-semibold">{anime.rating}</span>
            </div>
            <span class="px-3 py-1 bg-gray-800 rounded-full text-sm">{anime.releaseYear}</span>
            <span class="px-3 py-1 bg-primary-600 rounded-full text-sm capitalize">{anime.status}</span>
            <span class="px-3 py-1 bg-gray-800 rounded-full text-sm">{anime.totalEps} Episodes</span>
          </div>

          <div class="flex flex-wrap gap-2 mb-6">
            {#each anime.genres as genre}
              <span class="px-3 py-1 bg-gray-800 rounded-full text-sm">{genre}</span>
            {/each}
          </div>

          <p class="text-gray-300 text-lg leading-relaxed mb-8">
            {anime.description}
          </p>

          {#if episodes.length > 0}
            <Link 
              to="/v/{anime.id}?ep=1" 
              class="btn btn-primary inline-flex items-center gap-2"
            >
              <span>▶</span>
              Watch Now
            </Link>
          {/if}
        </div>
      </div>

      <!-- Episodes -->
      {#if episodes.length > 0}
        <div class="mt-12 mb-12">
          <h2 class="text-2xl font-bold mb-6">Episodes</h2>
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {#each episodes as episode}
              <Link 
                to="/v/{anime.id}?ep={episode.episodeNum}"
                class="card hover:scale-105 transition-transform"
              >
                <div class="relative aspect-video">
                  <img 
                    src={episode.thumbnail} 
                    alt="Episode {episode.episodeNum}"
                    class="w-full h-full object-cover"
                  />
                  <div class="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <span class="text-4xl">▶</span>
                  </div>
                </div>
                <div class="p-3">
                  <p class="font-semibold text-sm">Episode {episode.episodeNum}</p>
                  <p class="text-xs text-gray-400 truncate">{episode.title}</p>
                </div>
              </Link>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  </div>
{:else}
  <div class="min-h-screen flex items-center justify-center">
    <p class="text-2xl text-gray-400">Anime not found</p>
  </div>
{/if}
